import React, { useMemo, useState } from 'react';
import { FluidTabs } from '../../ui/fluid-tabs';
import { useLanguage } from '../../../contexts/useLanguage';

type MenuTab = 'all' | 'breakfast' | 'lunch' | 'souper';

type MenuItemId =
  | 'forestBreakfast'
  | 'lakeviewGrill'
  | 'campfireDessert'
  | 'mapleYogurt'
  | 'trailBurger'
  | 'lakeTrout';

const MENU_ITEM_IDS: MenuItemId[] = [
  'forestBreakfast',
  'lakeviewGrill',
  'campfireDessert',
  'mapleYogurt',
  'trailBurger',
  'lakeTrout',
];

const CATEGORY_BY_ITEM: Record<MenuItemId, Exclude<MenuTab, 'all'>> = {
  forestBreakfast: 'breakfast',
  lakeviewGrill: 'lunch',
  campfireDessert: 'souper',
  mapleYogurt: 'breakfast',
  trailBurger: 'lunch',
  lakeTrout: 'souper',
};

const PRICE_BY_ITEM: Record<MenuItemId, number> = {
  forestBreakfast: 18,
  lakeviewGrill: 29,
  campfireDessert: 12,
  mapleYogurt: 15,
  trailBurger: 24,
  lakeTrout: 34,
};

const HomeMenuSection: React.FC = () => {
  const { t, language } = useLanguage();
  const [activeTab, setActiveTab] = useState<MenuTab>('all');

  const priceFormatter = useMemo(
    () =>
      new Intl.NumberFormat(language === 'fr' ? 'fr-CA' : language === 'zh' ? 'zh-CN' : 'en-CA', {
        style: 'currency',
        currency: 'CAD',
      }),
    [language]
  );

  const sampleMenu = useMemo(
    () =>
      MENU_ITEM_IDS.map((id) => ({
        id,
        category: CATEGORY_BY_ITEM[id],
        name: t(`landing.menu.item.${id}.name`),
        description: t(`landing.menu.item.${id}.desc`),
        price: PRICE_BY_ITEM[id],
      })),
    [t, language]
  );

  const filteredMenu = useMemo(
    () => (activeTab === 'all' ? sampleMenu : sampleMenu.filter((item) => item.category === activeTab)),
    [activeTab, sampleMenu]
  );

  return (
    <section id="menu" className="bg-[#f9f5f0] py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <h2 className="petit-formal-script-regular petit-formal-script-menu-heading mb-2 font-bold text-[#3d2817]">
          {t('landing.menu.title')}
        </h2>
        <p className="petit-formal-script-menu-sub mb-8 max-w-full truncate overflow-hidden text-ellipsis whitespace-nowrap text-gray-600">
          {t('landing.menu.subtitle')}
        </p>

        <FluidTabs
          tabs={[
            { id: 'breakfast', label: t('landing.menu.tab.breakfast') },
            { id: 'lunch', label: t('landing.menu.tab.lunch') },
            { id: 'souper', label: t('landing.menu.tab.souper') },
            { id: 'all', label: t('landing.menu.tab.all') },
          ]}
          activeTab={activeTab}
          onTabChange={(tabId) => setActiveTab(tabId as MenuTab)}
          className="mb-4"
          tabFontClassName="petit-formal-script-menu-tabs"
        />

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {filteredMenu.map((item) => (
            <article key={item.id} className="rounded-xl border border-[#e1d2c1] bg-white p-5 shadow-sm">
              <div className="petit-formal-script-regular petit-formal-script-menu-dish mb-1 font-semibold text-gray-900">
                {item.name}
              </div>
              <p className="petit-formal-script-menu-body mb-4 text-gray-600">{item.description}</p>
              <div className="petit-formal-script-menu-price font-semibold text-[#3d2817]">
                {priceFormatter.format(item.price)}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HomeMenuSection;
